/**
 * Native Bridge Interface
 *
 * 네이티브 앱과 웹 간 양방향 통신을 위한 타입 안전한 브릿지 인터페이스
 *
 * @example
 * ```typescript
 * import { sendMessageToApp, useBridge } from '@/lib/bridge'
 *
 * // 직접 호출
 * const appInfo = await sendMessageToApp({
 *   group: 'app',
 *   action: 'getInfo',
 *   data: {}
 * })
 *
 * // React Hook 사용
 * const { call, loading, error } = useBridge('app', 'getInfo')
 * const appInfo = await call({})
 * ```
 */

// Core functions
export {
  appMessageHandler,
  cleanupPendingRequests,
  getPendingRequestsStatus,
  handleNativeMessage,
  initializeBridge,
  isBridgeInitialized,
  sendMessageToApp
} from './core'

// Error classes
export { BridgeError, isBridgeError, logBridgeError } from './errors'

// Platform utilities
export { detectPlatform, isNativeInterfaceAvailable, logPlatformInfo, sendToNative } from './platform'

// Native event handlers
export {
  addNativeEventListener,
  clearAllNativeEventListeners,
  getNativeEventListenersStatus,
  handleNativeEvent,
  removeNativeEventListeners
} from './events'

// Types
export type { BridgeErrorCode } from './errors'
export type { PlatformType } from './platform'
export type {
  ActionDefinition,
  ActionRequest,
  ActionResponse,
  BridgeActions,
  BridgeCallParams,
  BridgeMessage,
  BridgeResponse,
  GroupActions,
  GroupDefinition
} from './types'
export type {
  LifecycleAppState,
  LifecycleEvents,
  LifecycleScreenState,
  NativeEventAction,
  NativeEventData,
  NativeEventGroup,
  NativeEventGroups,
  NativeEventHandler,
  NativeEventMessage,
  NavigationEvents
} from './types'

// Version info
export const BRIDGE_VERSION = '1.0.0'
